import { ReactComponent as CartIcon } from "@/assets/cart.svg";
import { ReactComponent as FavoriteIcon } from "@/assets/favorite.svg";
import { ReactComponent as FavoriteWhiteIcon } from "@/assets/favorite_white.svg";
import { ReactComponent as SearchIcon } from "@/assets/search.svg";
import { useCommonContext } from "@/utils/context/common";
import { MICRO_APP_BASEURL } from "@/utils/platform";
import useNavigation, { SearchPageParams } from "@/utils/useNavigate";
import { Mask, Popover } from "antd-mobile";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useActivate } from "react-activation";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddCartSuccess from "../add-cart-success";
import styles from "./index.module.less";

interface IProps {
  zIndex?: number; // zIndex
  center?: React.ReactNode; // 中间的内容
  follow?: React.ReactNode; // 收藏内容
  backgroundColor?: string; // 背景色
  badgeBackgroundColor?: string; // 购物车数量背景色
  badgeFontColor?: string; // 购物车数量字体颜色
  searchPageParams?: SearchPageParams; // 搜索参数
  hideHomeText?: boolean; // 是否隐藏首页文字
  hideSearchIcon?: boolean; // 是否显示搜索图标
  hideCartIcon?: boolean; // 是否显示购物车图标
  hideFavoriteIcon?: boolean; // 是否显示收藏图标
  showShareIcon?: boolean; // 是否显示分享图标
  onClickShare?: () => void;
  showRightTitle?: string; // 是否显示右侧标题
  rightClick?: () => void; // 右边点击回掉
  isWhiteTheme?: boolean; //是否白色主题
  className?: string; // 自定义样式
  bgColorTrackScroll?: boolean; // 是否背景颜色随着滚动变化, 默认 `true`
}

export interface NavigationBarRef {
  /** 显示加购成功 */
  triggerAddCartSuccess: () => void;
}

/**
 * 顶部导航栏
 * @param props
 * 更新购物车方法
 * import Navigation from "@/components/top-navigation/Navigation";
 * import {CommonContext} from "@/utils/context";
 * import {EventName} from "@/utils/event/events";
 *
 * const { eventManager } = useCommonContext();
 * eventManager.publish(EventName.updateNavigationCart,"");
 */
const NavigationBar = forwardRef<NavigationBarRef, IProps>((props, ref) => {
  const {
    zIndex = 999,
    center,
    backgroundColor,
    badgeBackgroundColor,
    badgeFontColor,
    follow,
  } = props;
  const [search] = useSearchParams();
  const navigater = useNavigate();
  const shopId = search.get("shopId");
  const { cartNum = 0, setCartNum, currentRole, isExternal } = useCommonContext();
  const { navigate2SearchPage, navigate2Home, navigate2Product } = useNavigation();

  const [showAddCartSuccess, setShowAddCartSuccess] = useState(false);

  // 添加触发加购成功的方法
  const triggerAddCartSuccess = () => {
    setShowAddCartSuccess(true);
    setTimeout(() => {
      setShowAddCartSuccess(false);
    }, 3000);
  };

  useImperativeHandle(ref, () => ({
    triggerAddCartSuccess,
  }));

  // 前往购物车
  const goCart = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setShowAddCartSuccess(false);
    e?.stopPropagation();
    if (search.get("distributorId")) {
      navigater(
        `${MICRO_APP_BASEURL}/#/cart?shopId=${shopId}&channel=h5Mall&distributorId=${search.get(
          "distributorId",
        )}`,
      );
    } else {
      navigater(`${MICRO_APP_BASEURL}/#/cart?shopId=${shopId}&channel=h5Mall`);
    }
  };
  // 返回首页
  const goHome = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setShowAddCartSuccess(false);
    e?.stopPropagation();
    navigate2Home();
  };
  // 返回上一页
  const goBack = (e: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
    setShowAddCartSuccess(false);
    e.stopPropagation();
    if (history.length > 1) {
      const url = location.href;
      history.back();
      setTimeout(() => {
        if (location.href === url) {
          if (search.get("distributorId")) {
            navigater(
              `${MICRO_APP_BASEURL}/mx-shop-micro/home?shopId=${shopId}&channel=h5Mall&distributorId=${search.get(
                "distributorId",
              )}`,
            );
          } else {
            navigater(`${MICRO_APP_BASEURL}/mx-shop-micro/home?shopId=${shopId}&channel=h5Mall`);
          }
        }
      }, 500);
    } else {
      if (location.pathname === "/mx-shop-micro/questions-answers") {
        //转发直接进入商品问答页面，左上角返回到商详
        navigate2Product(
          {
            activityId: search.get("activityId") || "",
            activitySpuId: search.get("activitySpuId") || "",
            selectSkuId: search.get("selectSkuId") || "",
          },
          {},
          true,
        );
        return;
      }
      if (search.get("distributorId")) {
        navigater(
          `${MICRO_APP_BASEURL}/mx-shop-micro/home?shopId=${shopId}&channel=h5Mall&distributorId=${search.get(
            "distributorId",
          )}`,
        );
      } else {
        navigater(`${MICRO_APP_BASEURL}/mx-shop-micro/home?shopId=${shopId}&channel=h5Mall`);
      }
    }
  };

  const setupLongPress = (selector, callback) => {
    let longPressTimer: any = null;
    const element: HTMLElement = document.getElementById(selector) as HTMLElement;
    if (element) {
      element.addEventListener("touchstart", function (e) {
        longPressTimer = setTimeout(function () {
          callback();
        }, 3000);
      });

      element.addEventListener("touchend", function (e) {
        clearTimeout(longPressTimer);
      });
    }
  };

  useEffect(() => {
    setCartNum();
  }, []);
  useActivate(() => {
    setCartNum();
  });

  const containerDom = useRef<HTMLDivElement>(null);
  const placeHolderDom = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const bgColorTrackScroll = props.bgColorTrackScroll ?? true;
    if (!placeHolderDom.current || !containerDom.current || !bgColorTrackScroll) {
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        containerDom.current?.style.setProperty(
          "background-color",
          `rgba(255,255,255,${1 - entries[entries.length - 1].intersectionRatio})`,
        );
      },
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] },
    );
    observer.observe(placeHolderDom.current);

    return () => {
      observer?.disconnect();
    };
  }, [placeHolderDom.current, containerDom.current, props.bgColorTrackScroll]);

  const [showFavoriteGuide, setShowFavoriteGuide] = useState(false);

  const onClickShareIcon = () => {
    if (props.onClickShare) {
      props.onClickShare();
    }
  };

  return (
    <>
      <div
        ref={containerDom}
        id="navigation-container"
        className={`${styles.container} ${props.className}`}
        style={{
          zIndex: zIndex || 999,
          backgroundColor: backgroundColor || "rgba(255,255,255,0)",
        }}>
        <div className={`${props.isWhiteTheme ? styles["leftBox-white"] : styles["leftBox"]}`}>
          <img
            onClick={e => goBack(e)}
            className={styles.backIcon}
            src={
              props?.isWhiteTheme
                ? "https://akim-oss.aikucun.com/b1d5781111d84f7b3fe45a0852e59758cd7a87e5_1699586824224_62.png"
                : "https://akim-oss.aikucun.com/b07f427927175d0e745611f0d4342969f9f468f3_1703228563129_65.png"
            }
          />
          <div onClick={e => goHome(e)} className={styles.homeBox} id="navigation-home">
            <img
              className={styles.homeIcon}
              src={
                props?.isWhiteTheme
                  ? "https://akim-oss.aikucun.com/17ba0791499db908433b80f37c5fbc89b870084b_1699586824394_34.png"
                  : "https://akim-oss.aikucun.com/8cb8a10dbe75e006c04d3ff2036b4d61353908d6_1703228562634_67.png"
              }
            />
            {!(props.hideHomeText ?? false) && <span className={styles.homeText}>首页</span>}
          </div>
        </div>
        <div className={styles.midBox}>{center}</div>
        <div className={styles.rightBox}>
          {props.hideSearchIcon ? (
            <></>
          ) : (
            <div
              className={styles.rightIconWrapper}
              onClick={() => navigate2SearchPage(props.searchPageParams)}>
              <SearchIcon className={styles.rightIcon} />
            </div>
          )}
          {props.hideCartIcon ? (
            <></>
          ) : (
            <Popover
              visible={showAddCartSuccess}
              content={<AddCartSuccess />}
              className={styles.addCartPopover}
              placement="bottom"
              getContainer={() => document.getElementById("navigation-container") || document.body}>
              <div className={styles.rightIconWrapper} onClick={evt => goCart(evt)}>
                <CartIcon className={styles.rightIcon} />
                {cartNum > 0 && (
                  <div
                    className={styles.iconNum}
                    style={{ background: badgeBackgroundColor, color: badgeFontColor }}>
                    {cartNum}
                  </div>
                )}
              </div>
            </Popover>
          )}
          {(!(props.hideFavoriteIcon || isExternal) || follow) && (
            <div
              className={`${props.isWhiteTheme ? styles["rightIconWrapper-white"] : styles["rightIconWrapper"]} ${props.follow ? styles.follow : ""}`}>
              {follow}
              {!(props.hideFavoriteIcon || isExternal) && (
                <div onClick={() => setShowFavoriteGuide(true)}>
                  {props.isWhiteTheme ? (
                    <FavoriteWhiteIcon className={styles.favoriteWhite} />
                  ) : (
                    <FavoriteIcon className={styles.rightIcon} />
                  )}
                </div>
              )}
            </div>
          )}
          {props.showShareIcon && !isExternal ? (
            <div className={styles.rightIconWrapper} onClick={() => onClickShareIcon()}>
              <img
                className={styles.shareIcon}
                src={
                  ["A", "C"].includes(currentRole)
                    ? "https://akim-oss.aikucun.com/mshop/301668a14e3ce9ab72c4c7fa97d7d0bca3b0a77c_1723031060569_8.png"
                    : "https://akim-oss.aikucun.com/mshop/eeaaa563822b7c0c875f3c49e65c48e966db9716_1723717603263_33.png"
                }
              />
            </div>
          ) : (
            <></>
          )}
          {props?.showRightTitle && (
            <div className={styles.rightTitle} onClick={props?.rightClick}>
              {props?.showRightTitle}
            </div>
          )}
        </div>
      </div>
      <div ref={placeHolderDom} className={styles.placeholder} />

      <Mask visible={showFavoriteGuide} onMaskClick={() => setShowFavoriteGuide(false)}>
        <div className={styles.favoriteGuide}>
          <img
            src="https://akim-oss.aikucun.com/20f7fd64a1017ffe8e7f0853b4311b0d01d927c5_1747790428798_75.png"
            className={styles.image1}
          />
          <img
            src="https://akim-oss.aikucun.com/8aa1bca2a0b01b7403da049eff07d0f2a7a85eb0_1747790419544_46.png"
            className={styles.image2}
          />
          <img
            src="https://akim-oss.aikucun.com/8e8cd30f25aec7bd889ebe75c47a1c3153819bd8_1747790419615_98.png"
            className={styles.image3}
          />

          <div className={styles.footer}>
            <div onClick={() => setShowFavoriteGuide(false)} className={styles.button}>
              我知道了
            </div>
          </div>
        </div>
      </Mask>
    </>
  );
});

export default NavigationBar;
